import { useState } from 'react'
import { Plus, Trash2, Sparkles } from 'lucide-react'

interface ContextField {
  key: string
  value: string
}

interface ContextFormProps {
  onSubmit: (data: {
    contextText: string
    contextFields: Record<string, string>
    firstMessage: string
  }) => void
  loading?: boolean
}

export default function ContextForm({ onSubmit, loading }: ContextFormProps) {
  const [contextText, setContextText] = useState('')
  const [fields, setFields] = useState<ContextField[]>([])
  const [firstMessage, setFirstMessage] = useState('')

  const addField = () => setFields(prev => [...prev, { key: '', value: '' }])

  const removeField = (index: number) =>
    setFields(prev => prev.filter((_, i) => i !== index))

  const updateField = (index: number, prop: 'key' | 'value', val: string) =>
    setFields(prev => prev.map((f, i) => (i === index ? { ...f, [prop]: val } : f)))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!firstMessage.trim()) return

    const contextFields: Record<string, string> = {}
    for (const f of fields) {
      if (f.key.trim() && f.value.trim()) {
        contextFields[f.key.trim()] = f.value.trim()
      }
    }

    onSubmit({ contextText: contextText.trim(), contextFields, firstMessage: firstMessage.trim() })
  }

  return (
    <form onSubmit={handleSubmit} className="max-w-2xl mx-auto space-y-6">
      <div className="text-center mb-8">
        <div
          className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-4"
          style={{ backgroundColor: 'rgba(99,102,241,0.12)' }}
        >
          <Sparkles size={28} className="text-[#6366F1]" strokeWidth={1.25} />
        </div>
        <h2 className="text-xl font-semibold text-[#1B2559]">Nouvelle analyse IA</h2>
        <p className="text-sm text-[#8B8FA7] mt-1">
          Fournissez du contexte pour une analyse plus pertinente
        </p>
      </div>

      {/* Contexte */}
      <div>
        <label className="block text-sm font-medium text-[#1B2559] mb-1.5">Contexte</label>
        <textarea
          value={contextText}
          onChange={e => setContextText(e.target.value)}
          placeholder="Decrivez la situation, le probleme ou l'objectif de l'analyse..."
          rows={4}
          className="w-full rounded-lg border border-[#E2E4ED] px-3 py-2 text-sm text-[#1B2559] placeholder:text-[#8B8FA7] focus:outline-none focus:ring-2 focus:ring-[#6366F1]/30 focus:border-[#6366F1] resize-none"
        />
      </div>

      {/* Informations complementaires */}
      <div>
        <label className="block text-sm font-medium text-[#1B2559] mb-1.5">
          Informations complementaires
        </label>
        <div className="space-y-2">
          {fields.map((field, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                type="text"
                value={field.key}
                onChange={e => updateField(i, 'key', e.target.value)}
                placeholder="Titre (ex: Ancien site)"
                className="flex-1 rounded-lg border border-[#E2E4ED] px-3 py-2 text-sm text-[#1B2559] placeholder:text-[#8B8FA7] focus:outline-none focus:ring-2 focus:ring-[#6366F1]/30 focus:border-[#6366F1]"
              />
              <input
                type="text"
                value={field.value}
                onChange={e => updateField(i, 'value', e.target.value)}
                placeholder="Valeur (ex: www.ancien.com)"
                className="flex-1 rounded-lg border border-[#E2E4ED] px-3 py-2 text-sm text-[#1B2559] placeholder:text-[#8B8FA7] focus:outline-none focus:ring-2 focus:ring-[#6366F1]/30 focus:border-[#6366F1]"
              />
              <button
                type="button"
                onClick={() => removeField(i)}
                className="p-2 text-[#8B8FA7] hover:text-red-500 transition-colors"
              >
                <Trash2 size={16} strokeWidth={1.25} />
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={addField}
          className="mt-2 flex items-center gap-1.5 text-sm text-[#6366F1] hover:text-[#4F46E5] transition-colors"
        >
          <Plus size={14} strokeWidth={1.25} />
          Ajouter un champ
        </button>
      </div>

      {/* Premiere question */}
      <div>
        <label className="block text-sm font-medium text-[#1B2559] mb-1.5">
          Votre premiere question
        </label>
        <textarea
          value={firstMessage}
          onChange={e => setFirstMessage(e.target.value)}
          placeholder="Que voulez-vous analyser ?"
          rows={3}
          className="w-full rounded-lg border border-[#E2E4ED] px-3 py-2 text-sm text-[#1B2559] placeholder:text-[#8B8FA7] focus:outline-none focus:ring-2 focus:ring-[#6366F1]/30 focus:border-[#6366F1] resize-none"
        />
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={!firstMessage.trim() || loading}
        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-white bg-[#6366F1] hover:bg-[#4F46E5] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {loading ? (
          <span className="flex items-center gap-2">
            <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            Demarrage...
          </span>
        ) : (
          <>
            <Sparkles size={16} strokeWidth={1.25} />
            Demarrer l'analyse
          </>
        )}
      </button>
    </form>
  )
}
